/**
 * QueryError types, error classes and conversion helpers.
 *
 * Ported from the legacy UI kit (`src/core/api/queryError.ts`) and adapted to
 * plain JavaScript. A `QueryError` normalizes every request failure into one of
 * three discriminated error classes (network / 4xx request / server) so UI
 * components can render the appropriate message for each case.
 *
 * Every class is a real `Error` instance (`instanceof` works), carries a
 * `type`, `url`, `method`, optional `status`/`statusText`/`data`, exposes the
 * original error via `originalError` and serializes cleanly through `toJSON`.
 */

/**
 * Returns true when the given error is a `QueryRequestError` (4xx response).
 * @param {object} queryError - Any error (typically a QueryError instance).
 * @returns {boolean}
 */
export const isRequestError = (queryError) => queryError.type === 'RequestError';

/**
 * Returns true when the given error is one of the QueryError classes.
 * @param {Error} error - The error to check.
 * @returns {boolean}
 */
export const isQueryError = (error) =>
    error instanceof QueryNetworkErrorClass ||
    error instanceof QueryRequestErrorClass ||
    error instanceof QueryServerErrorClass;

/**
 * Converts an axios error into the matching QueryError class based on the
 * response status (no response / status 0 -> network error, 4xx -> request
 * error, anything else -> server error).
 * @param {import('axios').AxiosError} axiosError - The rejected axios error.
 * @returns {Promise<QueryError>}
 */
export const fromAxiosError = async (axiosError) => {
    const errorType = getQueryErrorTypeFromStatus(axiosError.response?.status);
    const url = axiosError.config.url;
    const method = (axiosError.config.method ?? 'GET').toUpperCase();

    switch (errorType) {
        case 'NetworkError':
            return new QueryNetworkErrorClass({
                originalError: axiosError,
                url,
                method,
            });

        case 'RequestError':
            return new QueryRequestErrorClass({
                status: axiosError.response.status,
                statusText: axiosError.response.statusText,
                data: axiosError.response.data,
                originalError: axiosError,
                url,
                method,
            });

        default:
            return new QueryServerErrorClass({
                status: axiosError.response.status,
                statusText: axiosError.response.statusText,
                originalError: axiosError,
                url,
                method,
            });
    }
};

/**
 * Converts a fetch `Request`/`Response` pair into the matching QueryError class.
 * When `response` is already a QueryError it is returned as-is, and a plain
 * `Error` is treated as a network error.
 * @param {Request} request - The original fetch request.
 * @param {Response|Error} response - The fetch response (or the rejected error).
 * @returns {Promise<QueryError>}
 */
export const fromFetchError = async (request, response) => {
    if (
        response instanceof QueryNetworkErrorClass ||
        response instanceof QueryRequestErrorClass ||
        response instanceof QueryServerErrorClass
    ) {
        return response;
    }

    if (response instanceof Error) {
        return new QueryNetworkErrorClass({
            url: request.url,
            method: request.method,
            originalError: response,
        });
    }

    const errorType = getQueryErrorTypeFromStatus(response.status);
    switch (errorType) {
        case 'NetworkError':
            return new QueryNetworkErrorClass({
                url: request.url,
                method: request.method,
            });

        case 'RequestError':
            return new QueryRequestErrorClass({
                status: response.status,
                statusText: response.statusText,
                data: await response.clone().json(),
                url: request.url,
                method: request.method,
            });

        default:
            return new QueryServerErrorClass({
                status: response.status,
                statusText: response.statusText,
                url: request.url,
                method: request.method,
            });
    }
};

/**
 * Factory to manually create a query request error.
 * Useful when an API call succeeds but the returned data is not what was
 * expected and needs to be treated as an error.
 * @param {object} options - Error details.
 * @param {number} [options.status] - HTTP status (defaults to 400).
 * @param {string} [options.statusText] - HTTP status text (defaults to 'BadRequest').
 * @param {*} options.data - The response payload.
 * @param {string} options.url - The request URL.
 * @param {string} options.method - The HTTP method.
 * @returns {QueryRequestErrorClass}
 */
export const customQueryRequestErrorFactory = ({
    status = 400,
    statusText = 'BadRequest',
    ...rest
}) =>
    new QueryRequestErrorClass({
        status,
        statusText,
        ...rest,
    });

/**
 * Maps a HTTP status to the QueryError type: no status or 0 is a network
 * error, 4xx a request error, everything else a server error.
 * @param {number|undefined} status - HTTP status.
 * @returns {'NetworkError'|'RequestError'|'ServerError'}
 */
const getQueryErrorTypeFromStatus = (status) => {
    if (status === 0 || status === undefined) {
        return 'NetworkError';
    }

    if (status >= 400 && status < 499) {
        return 'RequestError';
    }

    return 'ServerError';
};

export class QueryNetworkErrorClass extends Error {
    constructor({ originalError, method, url }) {
        super(`API request failure: Network error, ${method} ${url}`);
        Object.setPrototypeOf(this, QueryNetworkErrorClass.prototype);

        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, QueryNetworkErrorClass);
        }

        this.isQueryError = true;
        this.type = 'NetworkError';
        this.name = this.type;
        this.originalError = originalError;
        this.method = method;
        this.url = url;
    }

    toJSON() {
        return {
            name: this.name,
            message: this.message,
            type: this.type,
            url: this.url,
            method: this.method,
        };
    }
}

class QueryRequestErrorClass extends Error {
    constructor({ status, statusText, data, originalError, method, url }) {
        super(
            `API request failure: Bad request error: ${statusText}, ${status} ${method} ${url}`
        );
        Object.setPrototypeOf(this, QueryRequestErrorClass.prototype);

        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, QueryRequestErrorClass);
        }

        this.isQueryError = true;
        this.type = 'RequestError';
        this.name = this.type;
        this.originalError = originalError;
        this.status = status;
        this.statusText = statusText;
        this.data = data;
        this.method = method;
        this.url = url;
    }

    toJSON() {
        return {
            name: this.name,
            message: this.message,
            type: this.type,
            status: this.status,
            statusText: this.statusText,
            data: this.data,
            url: this.url,
            method: this.method,
        };
    }
}

class QueryServerErrorClass extends Error {
    constructor({ status, statusText, originalError, method, url }) {
        super(
            `API request failure: Server error, ${statusText}, ${status} ${method} ${url}`
        );
        Object.setPrototypeOf(this, QueryServerErrorClass.prototype);

        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, QueryServerErrorClass);
        }

        this.isQueryError = true;
        this.type = 'ServerError';
        this.name = this.type;
        this.originalError = originalError;
        this.status = status;
        this.statusText = statusText;
        this.method = method;
        this.url = url;
    }

    toJSON() {
        return {
            name: this.name,
            message: this.message,
            type: this.type,
            status: this.status,
            statusText: this.statusText,
            url: this.url,
            method: this.method,
        };
    }
}
